import * as z from 'zod/mini';
import type { Market } from '../config/domain-locale-map';
import type { TranslatorFor } from '../locales/messages';
import { formatPhoneNumber, phoneNumberIsValid } from './phonenumbers';

const stripSpaces = (s: string) => s.replace(/\s+/g, '');

// SE reg no: 3 letters (excl I, O, Q, V), 2 digits, and alnum (w/ same letter set), total 6 chars
export const swedishRegistrationNumberSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.string().check(
    // normalize first
    z.overwrite(stripSpaces),
    // length check
    z.refine((val: string) => val.length === 6, { message: t('invalidSwedishRegistrationNumber') }),
    // pattern check
    z.refine((val: string) => /^[A-HJ-PR-UW-Z]{3}\d{2}[A-HJ-PR-UW-Z0-9]$/i.test(val), {
      message: t('invalidSwedishRegistrationNumber'),
    }),
    // final normalization
    z.toUpperCase(),
  );

export const norwegianRegistrationNumberSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.string().check(
    z.overwrite(stripSpaces),
    z.refine((val: string) => val.length === 7, {
      message: t('invalidNorwegianRegistrationNumber'),
    }),
    z.refine((val: string) => /^[A-HJ-NPR-Z]{2}\d{5}$/i.test(val), {
      message: t('invalidNorwegianRegistrationNumber'),
    }),
    z.toUpperCase(),
  );

export const registrationNumberSchemaByMarket = (
  market: Market,
  t: TranslatorFor<'utils.validation'>,
) => {
  switch (market) {
    case 'wayke':
      return swedishRegistrationNumberSchema(t);
    case 'drive':
      return norwegianRegistrationNumberSchema(t);
    default:
      return swedishRegistrationNumberSchema(t);
  }
};

export const odometerReadingSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.number({ message: t('invalidOdometerReading') }).check(
    // keep explicit non-negative rule (0 allowed)
    z.refine((n: number) => Number.isFinite(n), { message: t('invalidOdometerReading') }),
    z.refine((n: number) => n >= 0, { message: t('invalidOdometerReadingNegative') }),
  );

export const swedishPostalCodeSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.string().check(
    // non-empty (treat whitespace-only as empty)
    z.refine((s: string) => s.trim().length >= 1, { message: t('invalidPostalCode') }),
    // normalize then exact length
    z.overwrite(stripSpaces),
    z.refine((s: string) => s.length === 5, { message: t('invalidPostalCode') }),
  );

export const norwegianPostalCodeSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.string().check(
    z.refine((s: string) => s.trim().length >= 1, { message: t('invalidPostalCode') }),
    z.overwrite(stripSpaces),
    z.refine((s: string) => s.length === 4, { message: t('invalidPostalCode') }),
  );

// Supports both SE & NO; normalize with formatter if available
export const phoneNumberSchema = (t: TranslatorFor<'utils.validation'>) =>
  z.string().check(
    z.refine((s: string) => s.length >= 1, { message: t('invalidPhoneNumber') }),
    z.refine((s: string) => phoneNumberIsValid(s), { message: t('invalidPhoneNumber') }),
    z.overwrite((val: string) => formatPhoneNumber(val) ?? val),
  );
